import { h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { buttonize } from '../../../../utils/a11y.utils';

import { selectIsAuthenticated } from '../../../../auth/store/auth.selectors';
import type { BuyItNowRequest } from '../../../../cart/models/cart.model';
import { AddToCartRequest } from '../../../../cart/models/cart.model';
import { useShopifyCurrency } from '../../../../common/currency/shopify-currency.hook';
import { IconCaret } from '../../../../common/icons/icon-caret';
import { IconComments } from '../../../../common/icons/icon-comments';
import { IconHeart } from '../../../../common/icons/icon-heart';
import { IconHeartOutline } from '../../../../common/icons/icon-heart-outline';
import { IconLock } from '../../../../common/icons/icon-lock';
import { IconShare } from '../../../../common/icons/icon-share';
import { selectBuyingNow } from '../../../../content/store/content.selectors';
import { buyItNow } from '../../../../content/store/content.thunk';
import { useTranslation } from '../../../../i18n';
import { NeedAccessButton } from '../../../../need-access/components/need-access-button/need-access-button.component';
import { NeedAccessContentType } from '../../../../need-access/need-access.interface';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { toastService } from '../../../../toast/toast.service';
import type { Post } from '../../../model/post.interface';
import type { UserActionRequest } from '../../../model/user-action.inteface';
import { ActionType } from '../../../model/user-action.inteface';
import { PerksHelper } from '../../../perks.helper';
import { selectPostCountsLoading } from '../../../store/perks.selectors';
import { postUserAction } from '../../../store/perks.thunk';
import { PerksPostTopComment } from '../perks-post-comments/perks-post-top-comment/perks-post-top-comment.component';

interface Props {
  post: Post;
  previewMode: boolean;
  showTopComment: boolean;
  onNeedAccessClick: () => void;
  goToPost: (comments?: boolean, focusInput?: boolean) => void;
}

export const PerksPostFooter = ({
  post,
  previewMode,
  showTopComment = false,
  onNeedAccessClick,
  goToPost,
}: Props) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const countsLoading: boolean = useAppSelector(selectPostCountsLoading(post.id));
  const buyingNow = useAppSelector(selectBuyingNow);
  const [selectedVariantId, setSelectedVariantId] = useState<number | string>();
  const { shopifyCurrency } = useShopifyCurrency();
  const { t } = useTranslation();

  useEffect(() => {
    if (PerksHelper.isProduct(post.contents) && post.contents[0].product?.variants?.length > 0) {
      setSelectedVariantId(post.contents[0].product.variants[0].id);
    }
  }, [post]);

  const handleLikePost = (like: boolean): void => {
    if (!post) {
      return;
    }
    if (post.hidden || !post.userHasAccess || previewMode) {
      onNeedAccessClick();
    } else {
      const action: UserActionRequest = {
        postId: post.id,
        actionType: like ? ActionType.LIKE : ActionType.REMOVE_LIKE,
      };
      dispatch(postUserAction(action));
    }
  };

  const handleCommentClick = (focusInput = false): void => {
    if (post.hidden || !post.userHasAccess) {
      onNeedAccessClick();
    } else {
      goToPost(true, focusInput);
    }
  };

  const buyNow = async (product: any) => {
    const variants = product.variants ?? [];
    const productVariantId =
      variants.length === 1 ? variants[0].id : parseInt(selectedVariantId, 10);
    const result = await dispatch(
      buyItNow({
        productVariantId,
        postId: previewMode ? null : post.id,
        product,
      } as BuyItNowRequest)
    );

    if (buyItNow.fulfilled.match(result)) {
      window.location.href = '/checkout';
    } else if (buyItNow.rejected.match(result)) {
      console.error('[Post > PostFooter] buyItNow failed:', result);
      toastService.error(t('common.error'), t('product.errorAddToCart'));
    }
  };

  const isMobileDevice = (): boolean => {
    return /Mobi|Android|iPhone|iPad|iPod/.test(navigator.userAgent);
  };

  const showTooltip = (): void => {
    const tooltip = document.getElementById(`single-tooltip-${post.id}`);
    tooltip?.classList.add('show');
    setTimeout(() => {
      tooltip?.classList.remove('show');
    }, 2000);
  };

  const share = ($event: PointerEvent | MouseEvent): void => {
    $event.preventDefault();

    const url = `${window.location.origin}${window.location.pathname}#/posts/${post.id}`;
    if (navigator.share && isMobileDevice()) {
      navigator
        .share({
          title: post.title,
          url,
        })
        .then(() => console.log('Successfully shared'))
        .catch((error) => console.log('Error sharing:', error));
    } else if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => showTooltip())
        .catch((err) => console.log('Failed to copy: ', err));
    } else {
      alert(t('common.browserShareError'));
    }
  };

  const shouldShowNeedAccess = useMemo((): boolean => {
    if (previewMode) {
      return false;
    }

    return (
      (PerksHelper.isProduct(post.contents) &&
        !post.userHasAccess &&
        (isAuthenticated || post.publicPreview)) ||
      post.hidden
    );
  }, [post, previewMode]);

  return (
    <div class={`single-post-footer ${post.hidden ? 'post-hidden' : ''}`}>
      <div class="single-post-actions-container">
        <div class="single-post-actions">
          <div
            class="single-post-likes"
            {...buttonize(() => handleLikePost(!post.userLiked), t(post.userLiked ? 'a11y.unlikePost' : 'a11y.likePost'))}
            aria-pressed={post.userLiked}
          >
            {!post.userLiked ? (
              <IconHeartOutline width={19} height={17} />
            ) : (
              <IconHeart width={19} height={17} />
            )}
            <span class="count">{countsLoading ? (post.counts?.value ? '-' : null) : post.counts?.value?.likes}</span>
          </div>

          {post.commentsEnabled && !showTopComment && (
            <div
              class="single-post-comments"
              {...buttonize(() => handleCommentClick(), t('a11y.viewComments'))}
            >
              <IconComments width={21} height={17} />
              <span class="count">{countsLoading ? (post.counts?.value ? '-' : null) : post.counts?.value?.comments}</span>
            </div>
          )}

          {!previewMode && (
            <div class="single-post-share" {...buttonize(share)}>
              <IconShare width={14} height={16} />
              <span>{t('common.share')}</span>
              <span id={`single-tooltip-${post.id}`} class="single-share-tooltip">
                {t('common.copied')}
              </span>
            </div>
          )}
        </div>

        {post.callToActionUrl && post.userHasAccess && (
          <div class="single-post-cta">
            <a class="btn button" href={post.callToActionUrl} target="_blank" rel="noopener">
              {post.callToActionText}
            </a>
          </div>
        )}

        {post.callToActionUrl && !post.userHasAccess && !previewMode && (
          <div class="single-post-cta post-cta-url">
            <NeedAccessButton
              contentType={NeedAccessContentType.POST}
              contentId={post.id}
              message={t('access.joinToUnlock')}
              showLock={true}
            />
          </div>
        )}

        {PerksHelper.isProduct(post.contents) && post.userHasAccess && (
          <div class="single-post-product-actions">
            {post.contents[0].product &&
              (() => {
                const product = post.contents[0].product;

                return (
                  <>
                    {product && product.variants?.length === 1 ? (
                      <p id={`single-product-price-${product.id}`} class="single-price-text">
                        {shopifyCurrency(product.variants[0].price)}
                      </p>
                    ) : (
                      <div class="select single-select-wrap single-variant-select">
                        <select
                          class="single-select select__select"
                          aria-label={product.title}
                          id={`single-variant-select-${product.id}`}
                          value={selectedVariantId}
                          onChange={(e) => setSelectedVariantId(e.target?.value)}
                        >
                          {product.variants?.map(
                            (variant) =>
                              variant.available && (
                                <option key={variant.id} value={variant.id}>
                                  {variant.title} - {shopifyCurrency(variant.price)}
                                </option>
                              )
                          )}
                        </select>
                        <IconCaret />
                      </div>
                    )}

                    <button
                      class="button btn"
                      disabled={!product.available || buyingNow}
                      onClick={() => buyNow(product)}
                    >
                      {!buyingNow ? (
                        <>
                          {product.available ? (
                            <>{post.callToActionText || t('product.buyNow')}</>
                          ) : (
                            <span>{t('product.soldOut')}</span>
                          )}
                          {product.variants?.length === 1 && (
                            <span class="mobile-price">
                              &nbsp;- {shopifyCurrency(product.variants[0].price)}
                            </span>
                          )}
                        </>
                      ) : (
                        <span>{t('product.processing')}</span>
                      )}
                    </button>
                  </>
                );
              })()}
          </div>
        )}

        {shouldShowNeedAccess && (
          <div
            class={
              PerksHelper.isProduct(post.contents) && !post.userHasAccess
                ? 'single-post-product-actions'
                : 'single-post-cta'
            }
          >
            <NeedAccessButton
              contentType={NeedAccessContentType.POST}
              contentId={post.id}
              message={
                PerksHelper.isProduct(post.contents) && !post.userHasAccess
                  ? t('access.joinForAccess')
                  : t('access.joinToUnlock')
              }
              showLock={true}
            />
          </div>
        )}

        {(post?.hidden || post?.publicPreview) && previewMode && (
          <div class="single-post-cta">
            <div class="single-locked-text">
              <IconLock />
              <span>{t('access.locked')}</span>
            </div>
          </div>
        )}
      </div>

      {!shouldShowNeedAccess && !((post?.hidden || post?.publicPreview) && previewMode) && (
        <>
          {post.commentsEnabled && showTopComment && (
            <PerksPostTopComment post={post} goToPost={handleCommentClick} />
          )}
        </>
      )}
    </div>
  );
};
